<template>
  <div class="solution-detail-page">
    <!-- Found: Full detail page -->
    <template v-if="solution && detail">
      <!-- Hero Banner -->
      <section class="hero-banner" :style="{ background: detail.heroGradient }">
        <div class="hero-image" :style="{ backgroundImage: `url(${detail.heroImage})` }"></div>
        <div class="hero-fade"></div>
        <div class="hero-content">
          <h1 class="hero-title">{{ solution.name }}</h1>
          <p class="hero-desc">{{ solution.descLong }}</p>
          <div class="hero-stats">
            <div v-for="(stat, i) in detail.stats" :key="i" class="stat-item">
              <p class="stat-value">{{ stat.value }}</p>
              <p class="stat-label">{{ stat.label }}</p>
            </div>
            <div
              v-for="i in detail.stats.length - 1"
              :key="`divider-${i}`"
              class="stat-divider"
            ></div>
          </div>
        </div>
      </section>

      <!-- Feature Sections (alternating layout) -->
      <section class="features-section">
        <div class="section-container">
          <div
            v-for="(feature, index) in detail.features"
            :key="index"
            class="feature-row"
            :class="{ 'feature-reverse': index % 2 === 1 }"
          >
            <div class="feature-image-wrap">
              <img :src="feature.image" :alt="feature.title" class="feature-image" />
            </div>
            <div class="feature-text">
              <div
                class="feature-icon-box"
                :style="{ background: feature.iconBg }"
              >
                <Icon :icon="feature.icon" :size="24" :color="feature.iconColor" />
              </div>
              <h2 class="feature-title">{{ feature.title }}</h2>
              <p class="feature-desc">{{ feature.desc }}</p>
              <div class="feature-tags">
                <div
                  v-for="(tag, ti) in feature.tags"
                  :key="ti"
                  class="feature-tag"
                >
                  <Icon :icon="tag.icon" :size="16" :color="feature.iconColor" />
                  <span>{{ tag.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Typical Scenarios -->
      <section v-if="detail.scenarios && detail.scenarios.length" class="scenarios-section">
        <div class="section-container">
          <h2 class="section-heading">典型应用场景</h2>
          <div class="scenarios-grid">
            <div
              v-for="(item, si) in detail.scenarios"
              :key="si"
              class="scenario-card card-hover"
            >
              <img :src="item.image" :alt="item.title" class="scenario-image" />
              <div class="scenario-body">
                <h4 class="scenario-title">{{ item.title }}</h4>
                <p class="scenario-desc">{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="cta-section">
        <div class="section-container">
          <div class="cta-actions">
            <button class="cta-btn-outline" @click="showTrialModal">咨询方案</button>
            <button class="cta-btn-primary" @click="showTrialModal">免费试用</button>
          </div>
        </div>
      </section>
    </template>

    <!-- Not Found Fallback -->
    <template v-else>
      <section class="not-found">
        <div class="not-found-content">
          <Icon icon="ep:warning-filled" :size="64" color="#9ca3af" />
          <h2 class="not-found-title">未找到该解决方案</h2>
          <p class="not-found-desc">请检查链接是否正确，或返回方案列表浏览其他方案</p>
          <button class="not-found-btn" @click="goBackToList">返回方案列表</button>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { Icon } from '@/components/Icon'
import { solutions, solutionDetails, type SolutionItem, type SolutionDetail } from '../data/portalData'
import { getPortalArticle } from '@/api/portal'
import { usePortalState } from '../usePortalState'
import { useRouter, useRoute } from 'vue-router'

defineOptions({ name: 'SolutionDetailPage' })

const router = useRouter()
const route = useRoute()
const { showTrialModal } = usePortalState()

const solution = ref<SolutionItem | undefined>()
const detail = ref<SolutionDetail | undefined>()

onMounted(async () => {
  const param = route.params.industry as string
  // First try static matching (for legacy URLs like /solutions/retail)
  let matched = solutions.find((s) => s.id === param)
  if (!matched) {
    // Try API lookup (for DB article IDs like 5201)
    try {
      const article = await getPortalArticle(param)
      if (article) {
        let extra: Record<string, any> = {}
        try { extra = JSON.parse(article.extra || '{}') } catch {}
        // Map article to SolutionItem
        const item: SolutionItem = {
          id: String(article.id),
          name: article.title,
          icon: extra.icon || 'lucide:layers',
          color: extra.color || '#0066FF',
          gradient: extra.gradient || 'linear-gradient(135deg,#0066FF,#0891B2)',
          desc: article.summary || '',
          descLong: article.content || article.summary || '',
          products: Array.isArray(extra.products) ? extra.products : [],
          cases: Number(extra.cases || 0),
          effect: extra.effect || ''
        }
        solution.value = item
        // Try to match static detail by solution name
        const nameMatch = solutions.find((s) => s.name === article.title)
        if (nameMatch) {
          detail.value = solutionDetails[nameMatch.id]
        }
        return
      }
    } catch {}
  }
  // Fallback: static data
  if (matched) {
    solution.value = matched
    detail.value = solutionDetails[param]
  }
})

function goBackToList() {
  router.push('/portal/solutions')
}
</script>

<style lang="scss" scoped>
.solution-detail-page {
  min-height: 100vh;
  background: #fff;
}

/* ===== Section Container ===== */
.section-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===== Hero Banner ===== */
.hero-banner {
  position: relative;
  height: 420px;
  overflow: hidden;
  display: flex;
  align-items: center;

  .hero-image {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0.15;
  }

  .hero-fade {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(to top, #fff, transparent);
  }

  .hero-content {
    position: relative;
    z-index: 2;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
    color: #fff;
    width: 100%;
  }

  .hero-title {
    font-size: 42px;
    font-weight: 800;
    margin-bottom: 16px;
  }

  .hero-desc {
    font-size: 18px;
    opacity: 0.85;
    max-width: 640px;
    line-height: 1.7;
  }
}

.hero-stats {
  display: flex;
  gap: 32px;
  margin-top: 32px;
  align-items: center;

  .stat-item {
    text-align: center;

    .stat-value {
      font-size: 36px;
      font-weight: 800;
    }

    .stat-label {
      font-size: 14px;
      opacity: 0.7;
    }
  }

  .stat-divider {
    width: 1px;
    height: 40px;
    background: rgba(255, 255, 255, 0.3);
  }
}

/* ===== Feature Sections ===== */
.features-section {
  padding: 80px 0;
}

.feature-row {
  display: flex;
  gap: 48px;
  align-items: center;
  margin-bottom: 80px;

  &:last-child {
    margin-bottom: 0;
  }

  &.feature-reverse {
    flex-direction: row-reverse;
  }

  .feature-image-wrap {
    flex: 1;
    border-radius: 16px;
    overflow: hidden;
    min-height: 320px;
  }

  .feature-image {
    width: 100%;
    height: 320px;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &:hover .feature-image {
    transform: scale(1.03);
  }

  .feature-text {
    flex: 1;
  }

  .feature-icon-box {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  .feature-title {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 16px;
    color: #111827;
  }

  .feature-desc {
    font-size: 15px;
    color: #6b7280;
    line-height: 1.8;
    margin-bottom: 24px;
  }
}

.feature-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  .feature-tag {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f9fafb;
    border-radius: 8px;
    padding: 12px 16px;
    transition: all 0.3s ease;

    &:hover {
      background: #f3f4f6;
    }

    span {
      font-size: 13px;
      font-weight: 500;
      color: #374151;
    }
  }
}

/* ===== CTA Section ===== */
.cta-section {
  padding: 32px 0 80px;
}

.cta-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 36px;
  border-radius: 10px;
  border: none;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  background: #0066ff;
  color: #fff;
  transition: all 0.3s ease;

  &:hover {
    background: #0044cc;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 102, 255, 0.3);
  }
}

.cta-btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 36px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #374151;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0066ff;
    color: #0066ff;
  }
}

/* ===== Scenarios Section ===== */
.scenarios-section {
  padding: 0 0 64px;
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.scenario-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
  }
}

.scenario-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.scenario-body {
  padding: 24px;
}

.scenario-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #111827;
}

.scenario-desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
}

/* ===== Not Found ===== */
.not-found {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}

.not-found-content {
  text-align: center;

  .not-found-title {
    font-size: 24px;
    font-weight: 700;
    color: #374151;
    margin: 24px 0 12px;
  }

  .not-found-desc {
    font-size: 15px;
    color: #9ca3af;
    margin-bottom: 28px;
  }
}

.not-found-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  border-radius: 10px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  background: #0066ff;
  color: #fff;
  transition: all 0.3s ease;

  &:hover {
    background: #0044cc;
    transform: translateY(-2px);
  }
}

/* ===== Responsive ===== */
@media (max-width: 1024px) {
  .feature-row {
    gap: 32px;

    .feature-image-wrap {
      min-height: 280px;
    }

    .feature-image {
      height: 280px;
    }

    .feature-title {
      font-size: 24px;
    }
  }

  .hero-banner {
    height: 360px;

    .hero-title {
      font-size: 36px;
    }
  }
}

@media (max-width: 768px) {
  .hero-banner {
    height: 320px;

    .hero-title {
      font-size: 28px;
    }

    .hero-desc {
      font-size: 15px;
    }
  }

  .hero-stats {
    gap: 20px;

    .stat-item .stat-value {
      font-size: 28px;
    }

    .stat-item .stat-label {
      font-size: 12px;
    }
  }

  .features-section {
    padding: 48px 0;
  }

  .feature-row {
    flex-direction: column !important;
    gap: 24px;
    margin-bottom: 48px;

    .feature-image-wrap {
      width: 100%;
      min-height: 220px;
    }

    .feature-image {
      height: 220px;
    }

    .feature-title {
      font-size: 22px;
    }

    .feature-desc {
      font-size: 14px;
    }
  }

  .feature-tags {
    gap: 8px;

    .feature-tag {
      padding: 10px 12px;
    }
  }

  .scenarios-grid {
    grid-template-columns: 1fr;
  }

  .scenario-image {
    height: 180px;
  }
}
</style>
